import { Card } from "@/types/v2";
import { ComboboxOption } from "@/components/Base/Combobox";

/**
 * Joins the parts that are actually present into a single space-separated
 * string. Every field these options are built from is optional and nullable,
 * so this is what keeps "undefined", "null" and stray separators out of the
 * rendered label.
 */
function joinPresent(parts: (string | number | null | undefined)[]): string {
  return parts
    .map((part) =>
      part === null || part === undefined ? "" : String(part).trim()
    )
    .filter((part) => part !== "")
    .join(" ");
}

/**
 * Builds the option shown in the task dialog's "Related Repair Order" picker.
 *
 * Deliberately reads canonical `Card` fields rather than going through
 * `getCardSection`/`shop.cardConfig`: the displayed slots are per-shop
 * configurable, so a shop whose primary slot is "Customer Name" produced a
 * label with no RO number in it and searching "#47" could never match
 * (WEBV3-163). This one control shows the repair order it claims to show.
 *
 * `value` stays the card's doc id — it is what the combobox hands back on
 * select and what gets persisted to `Task.relatedRO`.
 */
export function cardToTaskOption(card: Card): ComboboxOption {
  const roNumber = joinPresent([card.cardNumber]);
  const customer = joinPresent([
    card.customer?.firstName,
    card.customer?.lastName,
  ]);
  const vehicle = joinPresent([
    card.vehicle?.year,
    card.vehicle?.make,
    card.vehicle?.model,
  ]);

  // Manually-created cards can carry their whole identity in the hand-typed
  // slot fields (Card.primaryField/secondaryField/tertiaryField). This isn't
  // a rare case: CardForm requires a cardNumber, but a `Custom`-configured
  // slot (fieldMigration.ts never migrates those to a canonical field) means
  // text typed into it lives *only* here — customer name may never exist at
  // all for such a card.
  const rawSlotFields = [
    card.primaryField,
    card.secondaryField,
    card.tertiaryField,
  ];

  // searchText carries the slot text unconditionally: duplication inside a
  // search haystack is harmless, and findability is the whole point of this
  // module, so this is never gated on whether the label ends up using it.
  const slotText = joinPresent(rawSlotFields);

  // Label identity: the same slot fields, but with any individual field
  // dropped if it merely repeats the RO number already shown in the head
  // (e.g. a slot configured as "RO Number" on a manual card duplicates
  // card.cardNumber verbatim in card.primaryField). Decided per-field, not
  // on the joined string, so a real value in a sibling slot still survives.
  const slotIdentityText = joinPresent(
    rawSlotFields.map((field) => {
      if (field === null || field === undefined) {
        return "";
      }
      const trimmed = String(field).trim();
      return trimmed !== "" && trimmed === roNumber ? "" : trimmed;
    })
  );

  // Slot text is the identity whenever there's no customer name — with or
  // without an RO number — falling back to "Unknown customer" only when the
  // slot fields are empty too.
  const identity = customer || slotIdentityText || "Unknown customer";
  const head = [roNumber ? `RO #${roNumber}` : "", identity]
    .filter((segment) => segment !== "")
    .join(" — ");
  const label = vehicle ? `${head} (${vehicle})` : head;

  const keyTag = joinPresent([card.keyTag]);

  const searchTextBase = slotText ? `${label} ${slotText}` : label;

  return {
    value: card.id,
    label,
    // The key tag is searchable but not displayed — it would crowd a label
    // that already carries the RO number, customer and vehicle.
    searchText: keyTag ? `${searchTextBase} ${keyTag}` : searchTextBase,
  };
}

/**
 * Numeric RO number for sorting, or null when the card has no usable one.
 */
function roSortKey(card: Card): number | null {
  const raw = String(card.cardNumber ?? "").trim();
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Maps board cards to picker options, newest RO first. Cards with a blank or
 * non-numeric RO number sort last, alphabetically, so the unfiltered list is
 * scannable instead of arriving in Firestore subscription order.
 */
export function cardsToTaskOptions(cards: Card[]): ComboboxOption[] {
  return cards
    .map((card) => ({
      option: cardToTaskOption(card),
      sortKey: roSortKey(card),
    }))
    .sort((a, b) => {
      if (a.sortKey === null && b.sortKey !== null) return 1;
      if (a.sortKey !== null && b.sortKey === null) return -1;
      if (a.sortKey !== null && b.sortKey !== null && a.sortKey !== b.sortKey) {
        return b.sortKey - a.sortKey;
      }
      return a.option.label.localeCompare(b.option.label);
    })
    .map((entry) => entry.option);
}
